import { Events } from './focus-lock-event.mjs';
const instances = new Set();
export function getNodeHost(node) {
    if (!node)
        return;
    const root = node.getRootNode();
    if ('host' in root)
        return root.host;
}
function getParentOfLeaf(leafNode) {
    return (leafNode.parentElement ||
        (leafNode.getRootNode() &&
            leafNode.getRootNode().host));
}
// Build an ancestor tree similar to event.composedPath();
export function getComposedPathForLeaf(leafNode) {
    const result = [leafNode];
    let leaf = leafNode;
    let parent;
    while ((parent = getParentOfLeaf(leaf))) {
        result.push((leaf = parent));
    }
    return result;
}
// Missing support for SVG embedded content support in general. Focusable elements such that have `focusable=true` attribute, foreginObject embeds.
export function getFirstFocusable(elementArray, reverse, root) {
    var _a;
    const all = [...elementArray];
    if (reverse)
        all.reverse();
    const windowDefined = typeof window !== 'undefined';
    for (let i = 0; i < all.length; i++) {
        const el = all[i];
        // perform some basic tests on the element and it's parents style applied visibility
        if (windowDefined && !recursiveVisibilityCheck(el, root))
            continue;
        // Don't consider disabled, hidden elements at all.
        if (el.matches('[disabled], [hidden], [inert]'))
            continue;
        if (el.matches('button, a[href], input:not([type="hidden"]), select, textarea, audio, video, details, [tabindex], [contenteditable]:not([contenteditable="false"])') &&
            el.matches(':not([tabindex="-1"])')) {
            if ((_a = el.closest('fieldset')) === null || _a === void 0 ? void 0 : _a.hasAttribute('disabled'))
                continue; // if its part of a disabled fieldset.
            return el;
        }
        if (el.shadowRoot) {
            const embedded = getFirstFocusable(Array.from(el.shadowRoot.querySelectorAll('*')), reverse, el); // Let's find potential focusables within the shadowroot.
            if (embedded)
                return embedded;
        }
        if (el.tagName === 'SLOT') {
            const slotted = getFirstFocusable(Array.from(el.assignedElements({ flatten: true })), reverse, el); // Let's find potential slot-assigned focusable element.
            if (slotted)
                return slotted;
        }
    }
}
function registerLockChange(ev) {
    const ref = ev.SkapaFocusLockReference;
    if (ev.type === Events.Enable) {
        // Lock enabled on an instance.
        const previousActive = [...instances.values()].reverse()[0]; // The active focus lock that was activated before the current one.
        if (previousActive)
            previousActive.passive = true;
        instances.add(ref);
    }
    else {
        // Lock disabled on an instance.
        instances.delete(ref);
        // We need to remove the passive state
        const previousActive = [...instances.values()].reverse()[0]; // The active focus lock that was activated before the current one.
        if (previousActive)
            previousActive.passive = false;
    }
}
// Single notifier
function notifyPointerEvent(ev) {
    instances.forEach((instance) => instance.handlePointer(ev));
}
function notifyKeyEvent(ev) {
    if (ev.key === 'Escape') {
        // Maybe the locks should automatically break if escape is pressed?! Uncomment next line if yes. Currently the framework implementations are expected to handle this.
        // instances.forEach(instance => instance.disabled = true);
    }
    instances.forEach((instance) => instance.handleKeyboard(ev));
}
const uiEvents = ['mousedown', 'pointerup', 'touchstart', 'click'];
const keyEvents = ['keydown', 'keyup'];
const focusLockEvents = [Events.Enable, Events.Disable];
let listenersSet = false;
export function setDocumentListeners(on = true) {
    if (listenersSet === on)
        return; // if no change
    if (!on && instances.size)
        return; // keep the listeners as long as there are registered instances available.
    const listenerBind = (on ? document.addEventListener : document.removeEventListener).bind(document);
    uiEvents.forEach((eventType) => listenerBind(eventType, notifyPointerEvent, { capture: true }));
    keyEvents.forEach((eventType) => listenerBind(eventType, notifyKeyEvent));
    focusLockEvents.forEach((eventType) => listenerBind(eventType, registerLockChange));
    listenersSet = on;
}
/** This caching helper lessens performance hit by caching the visibility results of elements for the length of a frame.  */
let resetID = -1;
const visibilityCache = { cache: new WeakMap() };
function resetVisibilityCache() {
    resetID = -1;
    visibilityCache.cache = new WeakMap();
}
/** This function checks the element ancestry for applied hidden visibility
 * @returns `true` if visible and `false` if hidden.
 */
function recursiveVisibilityCheck(el, root) {
    if (!el)
        return false;
    if (el === root)
        return true;
    // Local reference to actual cache Map.
    const cache = visibilityCache.cache;
    // If the element has already been inspected in this frame.
    if (cache.has(el))
        return !!cache.get(el);
    // If the cache reset has not been triggered, then request it.
    if (resetID === -1) {
        resetID = requestAnimationFrame(resetVisibilityCache);
    }
    const { display, visibility } = window.getComputedStyle(el); // This may be an expensive call, but no alternative solution to test the rendering of the element is available. Perhaps some ARIA internal check?
    // Tested in chrome and its around a 10th of a milisecond. results are cached in an attemp to prevent overwhelming checks.
    if (display === 'none' || visibility === 'hidden') {
        cache.set(el, false);
        return false; // Not visible
    }
    const parent = el.parentNode;
    if (parent && parent === root) {
        // parent is already the focus lock region container, assume its visible.
        cache.set(el, true);
        return true;
    }
    if (parent && parent.nodeType === 1) {
        // If the parent is an element
        const result = recursiveVisibilityCheck(parent, root); // check for its visibility
        cache.set(el, result); // store it
        return result;
    }
    if (parent && parent.nodeType === 11 && 'host' in parent) {
        // if the parent is a shadowDom
        const result = recursiveVisibilityCheck(parent.host, root); // check for its visibility
        cache.set(el, result); // store it
        return result;
    }
    cache.set(el, true);
    return true;
}
export function getActiveLeaf(base = document) {
    if (!base.activeElement)
        return;
    if (!base.activeElement.shadowRoot)
        return base.activeElement;
    return (getActiveLeaf(base.activeElement.shadowRoot) ||
        base.activeElement);
}
